import { useEffect, useState } from "react";
import { RefreshCw, X, Sparkles } from "lucide-react";

/**
 * Mounts globally. Listens for an "sw:update-available" event dispatched by
 * index.js whenever a new service worker takes control. Shows a dismissable
 * banner inviting the user to reload the app to apply the update.
 */
export default function UpdateBanner() {
  const [version, setVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => {
      const v = (e && e.detail && e.detail.version) || "latest";
      setVersion(v);
      setDismissed(false);
    };
    window.addEventListener("sw:update-available", onUpdate);
    return () => window.removeEventListener("sw:update-available", onUpdate);
  }, []);

  if (!version || dismissed) return null;

  const reload = () => {
    try {
      // Best-effort: unregister caches of the previous SW so the next load is fully fresh
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
    } catch (_) {}
    window.location.reload();
  };

  return (
    <div
      data-testid="sw-update-banner"
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[60] flex justify-center px-3 pt-3 pointer-events-none"
    >
      <div className="pointer-events-auto flex items-center gap-3 max-w-xl w-full rounded-xl border border-[#E5B85C]/40 bg-[#0B0F17]/95 backdrop-blur-xl shadow-lg shadow-black/40 pl-4 pr-2 py-2.5">
        <div className="w-8 h-8 rounded-lg bg-[#E5B85C]/15 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-[#E5B85C]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
            Mise à jour
          </div>
          <div className="text-sm font-semibold text-white truncate">
            Nouvelle version disponible
          </div>
        </div>
        <button
          data-testid="sw-update-reload"
          onClick={reload}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-[11px] font-bold uppercase tracking-wide"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Mettre à jour
        </button>
        <button
          data-testid="sw-update-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Fermer"
          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
